import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertStatus,
  AlertTitle,
  Box,
  CloseButton,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../store";
import { clearMessage } from "../store/slices/authSlice";

type IAlertParams = {
  status?: AlertStatus;
  title?: string;
  message: string;
};

type IProps = {
  intialOpen?: boolean;
  autoHide?: boolean;
};

export const useAlert = ({ intialOpen, autoHide }: IProps = {}) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<AlertStatus>("error");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useAppDispatch();

  const clearAlert = useCallback(() => {
    setOpen(false);
    setTitle("");
    setMessage("");
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const showAlert = useCallback(
    ({ status, title, message }: IAlertParams) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setStatus(status ?? "error");
      setTitle(title ?? "");
      setMessage(message);
      setOpen(true);

      if (autoHide) {
        timeoutRef.current = setTimeout(() => {
          clearAlert();
        }, 5000); // Auto-hide after 5 second
      }
    },

    [autoHide, clearAlert]
  );

  const hideAlert = useCallback(
    (sec?: number) => {
      if (sec) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          clearAlert();
        }, sec * 1000);
      } else {
        clearAlert();
      }
    },
    [clearAlert]
  );

  useEffect(() => {
    if (intialOpen) {
      setOpen(true);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      // clear message whenever and hide alert on unmount
      dispatch(clearMessage());
      hideAlert();
    };
  }, []);

  const renderer = (
    <Box>
      <Alert borderRadius={".8rem"} status={status}>
        <AlertIcon />
        <Box width={"100%"}>
          {title && <AlertTitle>{title}</AlertTitle>}
          {message && (
            <AlertDescription fontSize={title ? "bodySmall" : "bodyMedium"}>
              {message}
            </AlertDescription>
          )}
        </Box>
        <CloseButton
          ml={"auto"}
          alignSelf="flex-start"
          position="relative"
          onClick={() => {
            setOpen(false);
          }}
        />
      </Alert>
    </Box>
  );

  return {
    render: open ? renderer : null,
    showAlert,
    open,
    setMessage,
    setOpen,
    hideAlert,
  };
};
